import React, { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthModal from './AuthModal';
import { useSEO } from '../hooks/useSEO';
import { Hero } from './landing';

// Lazy-load below-fold sections for faster initial paint
const HowItWorks = lazy(() => import('./landing/HowItWorks'));
const Features = lazy(() => import('./landing/Features'));
const Pricing = lazy(() => import('./landing/Pricing'));
const Testimonials = lazy(() => import('./landing/Testimonials'));
const FAQ = lazy(() => import('./landing/FAQ'));
const FinalCTA = lazy(() => import('./landing/FinalCTA'));
const Footer = lazy(() => import('./landing/Footer'));

interface PendingPurchase {
  productId: string;
  productName: string;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  useSEO({
    title: 'WFT Study Buddy | AI-Powered WFT Examentraining',
    description: 'Bereid je optimaal voor op je WFT examen met AI-gegenereerde oefenvragen op basis van officiële CDFD toetstermen. Schade Particulier, Schade Zakelijk, Basis, Hypotheken en meer.',
    canonicalUrl: '/',
  });
  const [authModalTab, setAuthModalTab] = useState<'signin' | 'signup'>('signin');
  const [pendingPurchase, setPendingPurchase] = useState<PendingPurchase | null>(null);

  const openSignIn = () => {
    setPendingPurchase(null); // Clear any pending purchase
    setAuthModalTab('signin');
    setIsAuthModalOpen(true);
  };

  const openSignUp = () => {
    setPendingPurchase(null); // Clear any pending purchase
    setAuthModalTab('signup');
    setIsAuthModalOpen(true);
  };

  // Handler voor purchase flow vanuit Hero
  const handlePurchase = (productId: string, productName: string) => {
    console.log('🛒 Purchase initiated:', { productId, productName });
    
    // Set pending purchase en open auth modal
    setPendingPurchase({ productId, productName });
    setAuthModalTab('signup'); // Default naar signup voor nieuwe gebruikers
    setIsAuthModalOpen(true);
  };

  const handleCloseAuthModal = () => {
    setIsAuthModalOpen(false);
    // Clear pending purchase na een korte delay (voor UX)
    setTimeout(() => setPendingPurchase(null), 300);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Auth Modal - nu met pending purchase support */}
      <AuthModal 
        isOpen={isAuthModalOpen}
        onClose={handleCloseAuthModal}
        defaultTab={authModalTab}
        pendingPurchase={pendingPurchase}
      />

      {/* Hero Section - nu met purchase handler */}
      <Hero 
        onNavigate={navigate}
        onSignIn={openSignIn}
        onSignUp={openSignUp}
        onPurchase={handlePurchase}
      />

      {/* Below-fold sections - lazy loaded */}
      <Suspense fallback={null}>
        <HowItWorks onNavigate={navigate} />
        <Features />
        <Pricing onNavigate={navigate} />
        <Testimonials />
        <FAQ />
        <FinalCTA onNavigate={navigate} />
        <Footer />
      </Suspense>

      {/* Custom Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-bounce-slow {
          animation: bounce 3s infinite;
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;